import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  User, CreditCard, Calendar, Clock, Shield, 
  Mail, Bell, Download, AlertTriangle, Check,
  Loader2, Crown, Zap, RefreshCw, Key, Smartphone, Copy, Eye, EyeOff
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { API, getAuthHeaders } from '../utils/api';

export default function Account() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [preferences, setPreferences] = useState({
    email_notifications: true,
    push_notifications: true,
    telegram_notifications: false
  });
  
  // 2FA States
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [setup2FAOpen, setSetup2FAOpen] = useState(false);
  const [disable2FAOpen, setDisable2FAOpen] = useState(false);
  const [twoFAPassword, setTwoFAPassword] = useState('');
  const [twoFACode, setTwoFACode] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [processing2FA, setProcessing2FA] = useState(false);
  const [setupStep, setSetupStep] = useState(1); // 1: password, 2: scan, 3: verify, 4: backup codes

  useEffect(() => {
    fetchAccountData();
    fetch2FAStatus();
  }, []);

  const fetchAccountData = async () => {
    try {
      const [userRes, subRes, historyRes] = await Promise.all([
        axios.get(`${API}/auth/me`, { withCredentials: true, headers: getAuthHeaders() }),
        axios.get(`${API}/subscription/status`, { withCredentials: true, headers: getAuthHeaders() }),
        axios.get(`${API}/payments/history`, { withCredentials: true, headers: getAuthHeaders() })
      ]);
      
      setUser(userRes.data);
      setSubscription(subRes.data);
      setPaymentHistory(historyRes.data.payments || []);
      
      if (userRes.data.notification_preferences) {
        setPreferences(userRes.data.notification_preferences);
      }
    } catch (error) {
      console.error('Failed to fetch account data:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetch2FAStatus = async () => {
    try {
      const response = await axios.get(`${API}/auth/2fa/status`, { withCredentials: true, headers: getAuthHeaders() });
      setTwoFactorEnabled(response.data.enabled);
    } catch (error) {
      console.error('Failed to fetch 2FA status:', error);
    }
  };

  const handleSetup2FA = async () => {
    if (!twoFAPassword) {
      toast.error('Please enter your password');
      return;
    }
    
    setProcessing2FA(true);
    try {
      const response = await axios.post(`${API}/auth/2fa/setup`, {
        password: twoFAPassword
      }, { withCredentials: true, headers: getAuthHeaders() });
      
      setQrCode(response.data.qr_code);
      setSecretKey(response.data.secret);
      setSetupStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to setup 2FA');
    } finally {
      setProcessing2FA(false);
    }
  };

  const handleVerify2FA = async () => {
    if (!twoFACode || twoFACode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    setProcessing2FA(true);
    try {
      const response = await axios.post(`${API}/auth/2fa/verify-setup`, {
        code: twoFACode
      }, { withCredentials: true, headers: getAuthHeaders() });
      
      setBackupCodes(response.data.backup_codes);
      setSetupStep(4);
      setTwoFactorEnabled(true);
      toast.success('Two-factor authentication enabled!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    } finally {
      setProcessing2FA(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!twoFACode || twoFACode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    setProcessing2FA(true);
    try {
      await axios.post(`${API}/auth/2fa/disable`, {
        code: twoFACode
      }, { withCredentials: true, headers: getAuthHeaders() });
      
      setTwoFactorEnabled(false);
      setDisable2FAOpen(false);
      setTwoFACode('');
      toast.success('Two-factor authentication disabled');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    } finally {
      setProcessing2FA(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const closeSetup2FADialog = () => {
    setSetup2FAOpen(false);
    setSetupStep(1);
    setTwoFAPassword('');
    setTwoFACode('');
    setQrCode('');
    setSecretKey('');
    setBackupCodes([]);
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      await axios.post(`${API}/subscription/cancel`, {}, { withCredentials: true, headers: getAuthHeaders() });
      toast.success('Subscription cancelled. You will retain access until the end of your billing period.');
      setCancelDialogOpen(false);
      fetchAccountData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const handlePreferenceChange = async (key, value) => {
    const newPrefs = { ...preferences, [key]: value };
    setPreferences(newPrefs);
    
    try {
      await axios.put(`${API}/user/preferences`, newPrefs, { withCredentials: true, headers: getAuthHeaders() });
      toast.success('Preferences updated');
    } catch (error) {
      toast.error('Failed to update preferences');
      setPreferences(preferences); // Revert
    }
  };

  const downloadReceipt = async (paymentId) => {
    try {
      const response = await axios.get(`${API}/payments/receipt/${paymentId}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `receipt-${paymentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download receipt');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto" />
          <p className="mt-4 text-slate-400">Loading account...</p>
        </div>
      </div>
    );
  }

  const isProUser = subscription?.plan && ['pro_monthly', 'pro_yearly'].includes(subscription.plan);
  const isPrivileged = subscription?.is_privileged || subscription?.plan === 'admin';
  const daysRemaining = subscription?.days_remaining || 0;
  const isTrial = subscription?.is_trial || subscription?.plan === 'trial';

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="account-page">
      {/* Page header */}
      <div>
        <h1 className="text-4xl font-bold font-['Manrope'] mb-2">Account Settings</h1>
        <p className="text-slate-400">Manage your profile, subscription, and preferences</p>
      </div>

      {/* Profile Card */}
      <Card className="bg-[#121214] border-zinc-800 p-6">
        <div className="flex items-start gap-6">
          <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-blue-500 rounded-2xl flex items-center justify-center text-3xl font-bold text-white">
            {user?.name?.[0] || 'U'}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-2xl font-bold">{user?.name}</h2>
              {(isProUser || isPrivileged) && (
                <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0">
                  <Crown className="w-3 h-3 mr-1" />
                  {isPrivileged ? 'ADMIN' : 'PRO'}
                </Badge>
              )}
              {isTrial && !isPrivileged && (
                <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                  <Zap className="w-3 h-3 mr-1" />
                  Trial
                </Badge>
              )}
            </div>
            <p className="text-slate-400 flex items-center gap-2">
              <Mail className="w-4 h-4" />
              {user?.email}
            </p>
            <p className="text-xs text-slate-500 mt-2">
              Member since {new Date(user?.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
      </Card>

      {/* Subscription Card */}
      <Card className="bg-[#121214] border-zinc-800 p-6">
        <div className="flex items-center gap-3 mb-6">
          <CreditCard className="w-6 h-6 text-emerald-400" />
          <h2 className="text-xl font-semibold">Subscription</h2>
        </div>

        {isPrivileged ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-xl border border-amber-500/20">
              <div>
                <p className="text-lg font-semibold flex items-center gap-2">
                  <Crown className="w-5 h-5 text-amber-400" />
                  Admin Access
                </p>
                <p className="text-sm text-slate-400">
                  Unlimited access to all features
                </p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-amber-400">∞</p>
                <p className="text-xs text-slate-500">unlimited</p>
              </div>
            </div>

            <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
              <p className="text-emerald-400 text-sm">
                You have full admin privileges with unlimited access to all TradeForge features.
              </p>
            </div>
          </div>
        ) : isProUser ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-emerald-500/10 to-blue-500/10 rounded-xl border border-emerald-500/20">
              <div>
                <p className="text-lg font-semibold capitalize">
                  {subscription.plan?.replace('_', ' ')}
                </p>
                <p className="text-sm text-slate-400">
                  {subscription.status === 'active' ? 'Active subscription' : 'Subscription ending'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-emerald-400">
                  {subscription.plan === 'pro_monthly' ? '$30' : '$300'}
                </p>
                <p className="text-xs text-slate-500">
                  {subscription.plan === 'pro_monthly' ? '/month' : '/year'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-zinc-900/50 rounded-lg">
                <div className="flex items-center gap-2 text-slate-400 mb-1">
                  <Calendar className="w-4 h-4" />
                  <span className="text-sm">Next billing</span>
                </div>
                <p className="font-semibold">
                  {subscription.subscription_end 
                    ? new Date(subscription.subscription_end).toLocaleDateString()
                    : 'N/A'}
                </p>
              </div>
              <div className="p-4 bg-zinc-900/50 rounded-lg">
                <div className="flex items-center gap-2 text-slate-400 mb-1">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm">Days remaining</span>
                </div>
                <p className="font-semibold">{daysRemaining} days</p>
              </div>
            </div>

            {subscription.status === 'active' && (
              <Button
                variant="outline"
                className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10"
                onClick={() => setCancelDialogOpen(true)}
                data-testid="cancel-subscription-btn"
              >
                Cancel Subscription
              </Button>
            )}

            {subscription.status === 'cancelled' && (
              <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <p className="text-amber-400 text-sm">
                  Your subscription has been cancelled. You'll retain access until {new Date(subscription.subscription_end).toLocaleDateString()}.
                </p>
              </div>
            )}
          </div>
        ) : isTrial ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl border border-blue-500/20">
              <div>
                <p className="text-lg font-semibold flex items-center gap-2">
                  <Zap className="w-5 h-5 text-blue-400" />
                  14-Day Free Trial
                </p>
                <p className="text-sm text-slate-400">
                  {subscription.status === 'active' ? 'Full access to all features' : 'Trial ended'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-blue-400">FREE</p>
                <p className="text-xs text-slate-500">limited time</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-zinc-900/50 rounded-lg">
                <div className="flex items-center gap-2 text-slate-400 mb-1">
                  <Calendar className="w-4 h-4" />
                  <span className="text-sm">Trial ends</span>
                </div>
                <p className="font-semibold">
                  {subscription.trial_end 
                    ? new Date(subscription.trial_end).toLocaleDateString()
                    : 'N/A'}
                </p>
              </div>
              <div className="p-4 bg-zinc-900/50 rounded-lg">
                <div className="flex items-center gap-2 text-slate-400 mb-1">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm">Days remaining</span>
                </div>
                <p className="font-semibold text-blue-400">{daysRemaining} days</p>
              </div>
            </div>

            <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
              <p className="text-emerald-400 text-sm mb-3">
                Enjoying TradeForge? Upgrade to Pro for uninterrupted access!
              </p>
              <Button 
                onClick={() => navigate('/pricing')}
                className="w-full bg-gradient-to-r from-emerald-500 to-blue-500 hover:opacity-90"
              >
                <Crown className="w-4 h-4 mr-2" />
                Upgrade to Pro - Save 17% on Yearly
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <Shield className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Free Plan</h3>
            <p className="text-slate-400 mb-6">Upgrade to Pro for unlimited access to all signals and features</p>
            <Button 
              onClick={() => navigate('/pricing')}
              className="bg-gradient-to-r from-emerald-500 to-blue-500 hover:opacity-90"
              data-testid="upgrade-btn"
            >
              <Crown className="w-4 h-4 mr-2" />
              Upgrade to Pro
            </Button>
          </div>
        )}
      </Card>

      {/* Notification Preferences */}
      <Card className="bg-[#121214] border-zinc-800 p-6">
        <div className="flex items-center gap-3 mb-6">
          <Bell className="w-6 h-6 text-blue-400" />
          <h2 className="text-xl font-semibold">Notification Preferences</h2>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-zinc-900/50 rounded-lg">
            <div>
              <p className="font-medium">Email Notifications</p>
              <p className="text-sm text-slate-400">Receive signals and updates via email</p>
            </div>
            <Switch
              checked={preferences.email_notifications}
              onCheckedChange={(v) => handlePreferenceChange('email_notifications', v)}
              data-testid="email-notifications-switch"
            />
          </div>

          <div className="flex items-center justify-between p-4 bg-zinc-900/50 rounded-lg">
            <div>
              <p className="font-medium">Push Notifications</p>
              <p className="text-sm text-slate-400">Browser push notifications for new signals</p>
            </div>
            <Switch
              checked={preferences.push_notifications}
              onCheckedChange={(v) => handlePreferenceChange('push_notifications', v)}
              data-testid="push-notifications-switch"
            />
          </div>

          <div className="flex items-center justify-between p-4 bg-zinc-900/50 rounded-lg">
            <div>
              <p className="font-medium">Telegram Notifications</p>
              <p className="text-sm text-slate-400">Get notified via Telegram bot</p>
            </div>
            <Switch
              checked={preferences.telegram_notifications}
              onCheckedChange={(v) => handlePreferenceChange('telegram_notifications', v)}
              data-testid="telegram-notifications-switch"
            />
          </div>
        </div>
      </Card>

      {/* Two-Factor Authentication */}
      <Card className="bg-[#121214] border-zinc-800 p-6">
        <div className="flex items-center gap-3 mb-6">
          <Key className="w-6 h-6 text-emerald-400" />
          <h2 className="text-xl font-semibold">Two-Factor Authentication (2FA)</h2>
        </div>

        <div className="p-4 bg-zinc-900/50 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${twoFactorEnabled ? 'bg-emerald-500/20' : 'bg-zinc-800'}`}>
                <Smartphone className={`w-6 h-6 ${twoFactorEnabled ? 'text-emerald-400' : 'text-slate-500'}`} />
              </div>
              <div>
                <p className="font-medium flex items-center gap-2">
                  Authenticator App
                  {twoFactorEnabled && (
                    <Badge className="bg-emerald-500/20 text-emerald-400 border-0">Enabled</Badge>
                  )}
                </p>
                <p className="text-sm text-slate-400">
                  {twoFactorEnabled 
                    ? 'Your account is protected with 2FA'
                    : 'Add an extra layer of security to your account'
                  }
                </p>
              </div>
            </div>
            {twoFactorEnabled ? (
              <Button 
                variant="outline" 
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                onClick={() => setDisable2FAOpen(true)}
                data-testid="disable-2fa-btn"
              >
                Disable 2FA
              </Button>
            ) : (
              <Button 
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => setSetup2FAOpen(true)}
                data-testid="enable-2fa-btn"
              >
                <Shield className="w-4 h-4 mr-2" />
                Enable 2FA
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Payment History */}
      <Card className="bg-[#121214] border-zinc-800 p-6">
        <div className="flex items-center gap-3 mb-6">
          <CreditCard className="w-6 h-6 text-purple-400" />
          <h2 className="text-xl font-semibold">Payment History</h2>
        </div>

        {paymentHistory.length > 0 ? (
          <div className="space-y-3">
            {paymentHistory.map((payment) => (
              <div 
                key={payment.transaction_id}
                className="flex items-center justify-between p-4 bg-zinc-900/50 rounded-lg"
                data-testid={`payment-${payment.transaction_id}`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    payment.status === 'completed' ? 'bg-emerald-500/20' : 'bg-amber-500/20'
                  }`}>
                    {payment.status === 'completed' ? (
                      <Check className="w-5 h-5 text-emerald-400" />
                    ) : (
                      <Clock className="w-5 h-5 text-amber-400" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium capitalize">{payment.plan_id?.replace('_', ' ')}</p>
                    <p className="text-sm text-slate-400">
                      {new Date(payment.created_at).toLocaleDateString()} • {payment.payment_method}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <p className="font-semibold">${payment.amount}</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => downloadReceipt(payment.transaction_id)}
                    className="text-slate-400 hover:text-white"
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <CreditCard className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No payment history yet</p>
          </div>
        )}
        
        {/* Refund Link */}
        <div className="mt-4 pt-4 border-t border-zinc-800">
          <Button
            variant="ghost"
            onClick={() => navigate('/refunds')}
            className="text-slate-400 hover:text-white"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Request a Refund
          </Button>
        </div>
      </Card>

      {/* Cancel Subscription Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="bg-[#121214] border-zinc-800">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-5 h-5" />
              Cancel Subscription
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Are you sure you want to cancel your subscription? You'll lose access to:
            </DialogDescription>
          </DialogHeader>
          
          <ul className="space-y-2 py-4">
            <li className="flex items-center gap-2 text-slate-300">
              <span className="w-1.5 h-1.5 bg-red-400 rounded-full" />
              Unlimited trading signals
            </li>
            <li className="flex items-center gap-2 text-slate-300">
              <span className="w-1.5 h-1.5 bg-red-400 rounded-full" />
              Priority notifications
            </li>
            <li className="flex items-center gap-2 text-slate-300">
              <span className="w-1.5 h-1.5 bg-red-400 rounded-full" />
              Advanced analytics
            </li>
          </ul>

          <p className="text-sm text-slate-500">
            You'll retain access until the end of your current billing period.
          </p>

          <DialogFooter className="gap-3">
            <Button
              variant="outline"
              onClick={() => setCancelDialogOpen(false)}
              className="border-zinc-700"
            >
              Keep Subscription
            </Button>
            <Button
              onClick={handleCancelSubscription}
              disabled={cancelling}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-cancel-btn"
            >
              {cancelling ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Cancelling...
                </>
              ) : (
                'Yes, Cancel'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Setup 2FA Dialog */}
      <Dialog open={setup2FAOpen} onOpenChange={closeSetup2FADialog}>
        <DialogContent className="bg-[#121214] border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-emerald-400" />
              {setupStep === 4 ? 'Backup Codes' : 'Setup Two-Factor Authentication'}
            </DialogTitle>
            {setupStep < 4 && (
              <DialogDescription className="text-slate-400">
                {setupStep === 1 && 'Enter your password to begin setup'}
                {setupStep === 2 && 'Scan the QR code with your authenticator app'}
                {setupStep === 3 && 'Enter the 6-digit code from your app'}
              </DialogDescription>
            )}
          </DialogHeader>
          
          <div className="py-4">
            {setupStep === 1 && (
              <div className="space-y-4">
                <div>
                  <Label className="text-sm text-slate-400">Password</Label>
                  <Input
                    type="password"
                    value={twoFAPassword}
                    onChange={(e) => setTwoFAPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="bg-zinc-900 border-zinc-800 mt-2"
                    data-testid="2fa-password-input"
                  />
                </div>
              </div>
            )}
            
            {setupStep === 2 && (
              <div className="space-y-4 text-center">
                {qrCode && (
                  <div className="bg-white p-4 rounded-lg inline-block mx-auto">
                    <img src={qrCode} alt="2FA QR Code" className="w-48 h-48" />
                  </div>
                )}
                <div className="p-3 bg-zinc-900 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">Manual entry key:</p>
                  <div className="flex items-center justify-center gap-2">
                    <code className="text-sm text-emerald-400 font-mono">{secretKey}</code>
                    <Button size="icon" variant="ghost" onClick={() => copyToClipboard(secretKey)}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <Button onClick={() => setSetupStep(3)} className="w-full bg-emerald-600 hover:bg-emerald-700">
                  I've scanned the code
                </Button>
              </div>
            )}
            
            {setupStep === 3 && (
              <div className="space-y-4">
                <div>
                  <Label className="text-sm text-slate-400">Verification Code</Label>
                  <Input
                    type="text"
                    maxLength={6}
                    value={twoFACode}
                    onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    className="bg-zinc-900 border-zinc-800 mt-2 text-center text-2xl tracking-widest font-mono"
                    data-testid="2fa-code-input"
                  />
                </div>
              </div>
            )}
            
            {setupStep === 4 && (
              <div className="space-y-4">
                <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <p className="text-amber-400 text-sm flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Save these backup codes! They can be used if you lose access to your authenticator app.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {backupCodes.map((code, i) => (
                    <div key={i} className="p-2 bg-zinc-900 rounded font-mono text-center text-sm">
                      {code}
                    </div>
                  ))}
                </div>
                <Button 
                  onClick={() => copyToClipboard(backupCodes.join('\n'))} 
                  variant="outline" 
                  className="w-full border-zinc-700"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy All Codes
                </Button>
              </div>
            )}
          </div>

          <DialogFooter>
            {setupStep === 1 && (
              <Button 
                onClick={handleSetup2FA} 
                disabled={processing2FA || !twoFAPassword}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {processing2FA ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Continue
              </Button>
            )}
            {setupStep === 3 && (
              <Button 
                onClick={handleVerify2FA} 
                disabled={processing2FA || twoFACode.length !== 6}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="verify-2fa-btn"
              >
                {processing2FA ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Verify & Enable
              </Button>
            )}
            {setupStep === 4 && (
              <Button onClick={closeSetup2FADialog} className="bg-emerald-600 hover:bg-emerald-700">
                <Check className="w-4 h-4 mr-2" />
                Done
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disable 2FA Dialog */}
      <Dialog open={disable2FAOpen} onOpenChange={setDisable2FAOpen}>
        <DialogContent className="bg-[#121214] border-zinc-800 max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-5 h-5" />
              Disable 2FA
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Enter your authenticator code to disable two-factor authentication.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label className="text-sm text-slate-400">Verification Code</Label>
            <Input
              type="text"
              maxLength={6}
              value={twoFACode}
              onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, ''))}
              placeholder="000000"
              className="bg-zinc-900 border-zinc-800 mt-2 text-center text-2xl tracking-widest font-mono"
              data-testid="disable-2fa-code-input"
            />
          </div>

          <DialogFooter className="gap-3">
            <Button variant="outline" onClick={() => { setDisable2FAOpen(false); setTwoFACode(''); }} className="border-zinc-700">
              Cancel
            </Button>
            <Button 
              onClick={handleDisable2FA} 
              disabled={processing2FA || twoFACode.length !== 6}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-disable-2fa-btn"
            >
              {processing2FA ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Disable 2FA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
